import Vue from 'vue'
import { Mixin } from 'vue-mixin-decorator'
import { getRequest, postRequest, patchRequest, deleteRequest } from '@/plugins/api-axios'
import { getURL } from '@/utils/axios'
import { deserialise, serialise } from 'kitsu-core'

@Mixin
export default class ObservationMixin extends Vue {
    url_observation = process.env.VUE_APP_OBSERVATIONS_BASE_URL

    get headersmixins() {
        return {
            'Content-Type': 'application/vnd.api+json',
        }
    }

    obsIncludes =
        'mainAttachment,attachments,reporter,closedBy,responsibleUser,responsibleLeader,contractors,category.parent,cause.parent,location.parent,locationDrawing,project,restrictedTo,sharedWith'

    // OBSERVATIONS
    async getObservationTemplateByProject(projectId: string, isAnonymousUser = false) {
        const { data } = await getRequest(
            getURL({
                path: `observations`,
                base: this.url_observation,
                params: {
                    include: this.obsIncludes,
                    skipLinks: '',
                    'filter[templateForProject]': projectId,
                },
            }),
            isAnonymousUser ? { Authorization: 'Session kAbU3KEwjUQTJIJIaPI8qt' } : {}
        )

        const observationTemplates = deserialise(data).data

        return observationTemplates[0] ?? null
    }

    async getObservations(params: any) {
        let url = `${this.url_observation}/observations?include=project,reporter,closedBy,mainAttachment,responsibleUser,responsibleLeader,contractors&skipLinks`

        let res = await getRequest(url, this.headersmixins, params)

        let observations: any = {}
        let users: any = {}
        let attachments: any = {}
        let companies: any = {}
        res.data.data.map((item: any) => {
            observations['' + item.id] = item
        })

        res.data.included.map((item: any) => {
            switch (item.type) {
                case 'user':
                    users[item.id] = item
                    break
                case 'attachment':
                    attachments[item.id] = item
                    break
                case 'company':
                    companies[item.id] = item
                    break
            }
        })

        this.$store.commit('observations', { ...this.$store.getters.observations, ...observations })
        this.$store.commit('observationUsers', {
            ...this.$store.getters.observationUsers,
            ...users,
        })
        this.$store.commit('observationAttachments', {
            ...this.$store.getters.observationAttachments,
            ...attachments,
        })
        this.$store.commit('observationCompanies', {
            ...this.$store.getters.observationCompanies,
            ...companies,
        })
        return res.data
    }

    async getObservation(id: string) {
        const { data } = await getRequest(
            getURL({
                path: `observations/${id}`,
                base: this.url_observation,
                params: {
                    include: this.obsIncludes,
                    skipLinks: '',
                },
            })
        )

        if (data) {
            const observation = deserialise(data).data
            this.$store.commit('updatedObservation', observation)
            return observation
        }

        return data
    }

    async postObservation(payload: any, isAnonymousUser = false) {
        const { data } = await postRequest(
            getURL({
                path: `observations`,
                base: this.url_observation,
                params: {
                    include: this.obsIncludes,
                    skipLinks: '',
                },
            }),
            isAnonymousUser ? { Authorization: 'Session kAbU3KEwjUQTJIJIaPI8qt' } : {},
            payload.data
        )

        if (data) {
            const observation = deserialise(data).data
            this.$store.commit('addedObservation', observation)
            return observation
        }

        return data
    }

    async patchObservation(body: any, isAnonymousUser = false) {
        const { data } = await patchRequest(
            getURL({
                path: `observations/${body.data.id}`,
                base: this.url_observation,
                params: {
                    include: this.obsIncludes,
                    skipLinks: '',
                },
            }),
            isAnonymousUser ? { Authorization: 'Session kAbU3KEwjUQTJIJIaPI8qt' } : {},
            body.data
        )

        if (data) {
            const observation = deserialise(data).data
            this.$store.commit('updatedObservation', observation)
            return observation
        }

        return data
    }

    async deleteObservation(observation: any) {
        await deleteRequest(
            getURL({
                path: `observations/${observation.id}`,
                base: this.url_observation,
            }),
            {}
        )

        this.$store.commit('deletedObservation', observation)

        return
    }

    async publishPrivateObservation(id: string, anonymous = false, isAnonymousUser = false) {
        const { data } = await postRequest(
            getURL({
                path: `observations/${id}/${anonymous ? 'publishAnonymously' : 'publish'}`,
                base: this.url_observation,
                params: {
                    include: this.obsIncludes,
                    skipLinks: '',
                },
            }),
            isAnonymousUser ? { Authorization: 'Session kAbU3KEwjUQTJIJIaPI8qt' } : {}
        )

        if (data) {
            const observation = deserialise(data).data
            this.$store.commit('updatedObservation', observation)
            return observation
        }

        return data
    }

    // Attachments
    async postAttachmentObservation(payload: any, isAnonymousUser = false) {
        const { data } = await postRequest(
            getURL({
                path: `attachments`,
                base: this.url_observation,
                params: {
                    skipLinks: '',
                },
            }),
            isAnonymousUser ? { Authorization: 'Session kAbU3KEwjUQTJIJIaPI8qt' } : {},
            payload.data
        )

        return data ? deserialise(data).data : data
    }

    async patchObservationAttachment(payload: any) {
        const { data } = await patchRequest(
            getURL({
                path: `attachments/${payload.data.id}`,
                base: this.url_observation,
                params: {
                    skipLinks: '',
                },
            }),
            {},
            payload.data
        )

        return data ? deserialise(data).data : data
    }

    async deleteObservationAttachment(attachmentId: string) {
        await deleteRequest(
            getURL({
                path: `attachments/${attachmentId}`,
                base: this.url_observation,
            }),
            {}
        )

        return attachmentId
    }

    // CATEGORIES
    async getObservationCategories(groupId: string | null, projectId: string | null) {
        const { data } = await getRequest(
            getURL({
                path: `categories`,
                base: this.url_observation,
                params: {
                    include: 'project,parent',
                    skipLinks: '',
                    ...(groupId && { 'filter[projectGroup]': groupId }),
                    ...(!groupId && projectId && { 'filter[project]': projectId }),
                },
            })
        )

        const categories = deserialise(data).data
        this.$store.commit('observationCategories', categories)
        return categories
    }

    async getCategories(params: any) {
        let url = `${this.url_observation}/categories`
        let res = await getRequest(url, this.headersmixins, params)
        let categories: any = {}
        res.data.data.map((item: any) => {
            categories[item.id] = item
        })
        this.$store.commit('observationCategories', {
            ...this.$store.getters.observationCategories,
            ...categories,
        })
    }

    async patchCategory(params: any) {
        let url = `${this.url_observation}/categories/${params.data.id}`
        await patchRequest(url, this.headersmixins, params.data)
    }

    async postCategory(data: any) {
        let url = `${this.url_observation}/categories`
        await postRequest(url, this.headersmixins, data)
    }

    // CAUSES
    async getObservationCauses(groupId: string | null, projectId: string | null) {
        const { data } = await getRequest(
            getURL({
                path: `causes`,
                base: this.url_observation,
                params: {
                    include: 'project,parent',
                    skipLinks: '',
                    ...(groupId && { 'filter[projectGroup]': groupId }),
                    ...(!groupId && projectId && { 'filter[project]': projectId }),
                },
            })
        )

        const causes = deserialise(data).data
        this.$store.commit('observationCauses', causes)
        return causes
    }

    async getCauses(params: any) {
        let url = `${this.url_observation}/causes`
        let res = await getRequest(url, this.headersmixins, params)
        let causes: any = {}
        res.data.data.map((item: any) => {
            causes[item.id] = item
        })
        this.$store.commit('observationCauses', {
            ...this.$store.getters.observationCauses,
            ...causes,
        })
    }

    async patchCause(params: any) {
        let url = `${this.url_observation}/causes/${params.data.id}`
        await patchRequest(url, this.headersmixins, params.data)
    }

    async postCause(data: any) {
        let url = `${this.url_observation}/causes`
        await postRequest(url, this.headersmixins, data)
    }

    async getObservationStatistics(params: any) {
        const { data } = await getRequest(
            getURL({
                path: `observationStatistics`,
                base: this.url_observation,
                params: {
                    include: 'groupResource',
                    skipLinks: '',
                    ...params,
                },
            })
        )

        const stats = deserialise(data).data
        return stats
    }

    // PROJECT
    async getObservationProject(id: any) {
        let url = `${this.url_observation}/projects/${id}?include=settings`
        let res = await getRequest(url, this.headersmixins)

        let projects: any = { [res.data.data.id]: res.data.data }
        let settings: any = {}

        res.data.included.map((item: any) => {
            switch (item.type) {
                case 'projectSettings':
                    settings[item.id] = item
                    break
            }
        })

        this.$store.commit('observationProjects', {
            ...this.$store.getters.observationProjects,
            ...projects,
        })
        this.$store.commit('observationProjectSettings', {
            ...this.$store.getters.observationProjectSettings,
            ...settings,
        })
        return res.data.data
    }

    async getObservationProjects(params: any) {
        let url = `${this.url_observation}/projects?include=settings`
        let res = await getRequest(url, this.headersmixins, params)

        let projects: any = {}
        let settings: any = {}

        res.data.data.map((item: any) => {
            projects[item.id] = item
        })
        res.data.included.map((item: any) => {
            switch (item.type) {
                case 'projectSettings':
                    settings[item.id] = item
                    break
            }
        })

        this.$store.commit('observationProjects', {
            ...this.$store.getters.observationProjects,
            ...projects,
        })
        this.$store.commit('observationProjectSettings', {
            ...this.$store.getters.observationProjectSettings,
            ...settings,
        })
        return res.data.data
    }

    async patchObservationProjectSettings(params: any) {
        let url = `${this.url_observation}/projectSettings/${params.data.id}`
        let res = await patchRequest(url, this.headersmixins, params.data)
        let settings: any = { [res.data.data.id]: res.data.data }
        this.$store.commit('observationProjectSettings', {
            ...this.$store.getters.observationProjectSettings,
            ...settings,
        })
        return res.data.data
    }

    // USER
    async searchObservationUsers(params: any) {
        let url = `${this.url_observation}/users`
        let res = await getRequest(url, this.headersmixins, params)
        return res.data.data
    }

    // COMPANY
    async searchObservationCompanies(params: any) {
        let url = `${this.url_observation}/companies`
        let res = await getRequest(url, this.headersmixins, params)
        return res.data.data
    }

    async getObservationAttachment(id: string) {
        let url = `${this.url_observation}/attachments/${id}`

        let res = await getRequest(url, this.headersmixins)

        return res.data.data
    }

    async getObservationProjectSettings(id: string) {
        let url = `${this.url_observation}/projectSettings/${id}`

        let res = await getRequest(url, this.headersmixins)

        return res.data.data
    }

    contractorUrl = process.env.VUE_APP_CONTRACTOR_BASE_URL

    contractorHierarchiesIncluded = 'nodes.parent'

    async getContractorHierarchiesByProject(projectId: string) {
        const { data } = await getRequest(
            getURL({
                path: `contractorHierarchies/${projectId}`,
                base: this.contractorUrl,
                params: {
                    include: this.contractorHierarchiesIncluded,
                    skipLinks: '',
                },
            })
        )

        const contractorHierarchies = deserialise(data).data

        this.$store.commit('contractorHierarchy', { id: projectId, data: contractorHierarchies })

        return contractorHierarchies
    }
}
